/**
 * Named palette → base RGB. The same base drives both modes: fills use a
 * low-alpha wash, borders a stronger line. 'neutral' is the default surface —
 * a solid card (not a white wash) so it reads on the dark doc page.
 */
export const CALLOUT_PALETTE = {
  // Swatch preview only — real neutral fill/border use NEUTRAL_SURFACE below
  neutral: [22, 26, 32],
  gray: [160, 165, 180],
  brown: [190, 142, 100],
  orange: [255, 150, 64],
  yellow: [255, 197, 61],
  lime: [172, 210, 60],
  green: [52, 199, 123],
  teal: [45, 200, 190],
  cyan: [0, 217, 255],
  blue: [56, 149, 255],
  indigo: [116, 125, 255],
  purple: [167, 139, 250],
  pink: [240, 120, 190],
  red: [255, 94, 94],
};

/** Default callout card — matches the doc chrome (page #121519) */
export const NEUTRAL_CALLOUT_FILL = '#161A20';
export const NEUTRAL_CALLOUT_BORDER = '#21242A';

export const CALLOUT_COLORS = Object.keys(CALLOUT_PALETTE);

/** Fill / outline / both / invisible grouping. Missing prop → fill. */
export const CALLOUT_SURFACES = ['fill', 'border', 'both', 'none'];

export const resolveCalloutSurface = (props) =>
  CALLOUT_SURFACES.includes(props?.surface) ? props.surface : 'fill';

export const SURFACE_LABELS = {
  fill: 'Fill',
  border: 'Border',
  both: 'Both',
  none: 'None',
};

export const SURFACE_TITLES = {
  fill: 'Fill only',
  border: 'Border only',
  both: 'Fill and border',
  none: 'No fill or border',
};

export const showsFill = (surface) => surface === 'fill' || surface === 'both';
export const showsBorder = (surface) => surface === 'border' || surface === 'both';

/** '#rgb' / '#rrggbb' (hash optional) → [r,g,b], or null if not valid hex */
export const parseHexColor = (value) => {
  const m = String(value || '').trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
};

export const colorToRgb = (color) => CALLOUT_PALETTE[color] || parseHexColor(color);

/**
 * Accent from a colored FontAwesome icon (not emoji / custom upload / inherit).
 * Used to tint bullets, numbered markers, checkboxes, etc. inside the callout.
 */
export const getCalloutIconAccent = (icon) => {
  if (!icon || typeof icon !== 'object') return null;
  if (icon.type !== 'fontawesome') return null;
  const c = icon.color;
  if (!c || c === 'inherit') return null;
  return c;
};

const tintBorder = (rgb, alpha) =>
  `inset 0 0 0 1px rgba(${rgb.join(',')}, ${alpha})`;

/** Inline fill/border for the callout box. Quotes keep their CSS bar. */
export const calloutSurfaceStyle = ({ isQuote, isNone, isNeutral, surface, rgb }) => {
  if (isQuote) return undefined;
  if (isNone) return { background: 'transparent', boxShadow: 'none' };
  if (isNeutral) {
    return {
      background: showsFill(surface) ? NEUTRAL_CALLOUT_FILL : 'transparent',
      boxShadow: showsBorder(surface)
        ? `inset 0 0 0 1px ${NEUTRAL_CALLOUT_BORDER}`
        : 'none',
    };
  }
  return {
    background: showsFill(surface)
      ? `rgba(${rgb.join(',')}, 0.15)`
      : 'transparent',
    boxShadow: showsBorder(surface)
      ? tintBorder(rgb, surface === 'border' ? 0.32 : 0.22)
      : 'none',
  };
};

/** Swatch fill/border for a Style-menu surface mode. */
export const calloutModePreviewStyle = ({ mode, isNeutral, rgb }) => {
  if (mode === 'none') return undefined;
  if (isNeutral) {
    return {
      background: showsFill(mode) ? NEUTRAL_CALLOUT_FILL : 'transparent',
      boxShadow: showsBorder(mode)
        ? `inset 0 0 0 1.5px ${NEUTRAL_CALLOUT_BORDER}`
        : 'none',
    };
  }
  return {
    background: showsFill(mode)
      ? `rgba(${rgb.join(',')}, 0.35)`
      : 'transparent',
    boxShadow: showsBorder(mode)
      ? `inset 0 0 0 1.5px rgba(${rgb.join(',')}, 0.45)`
      : 'none',
  };
};

export const calloutColorSwatchStyle = (c) =>
  c === 'neutral'
    ? {
        background: NEUTRAL_CALLOUT_FILL,
        boxShadow: `inset 0 0 0 1px ${NEUTRAL_CALLOUT_BORDER}`,
      }
    : {
        background: `rgba(${CALLOUT_PALETTE[c].join(',')}, 0.65)`,
      };

export const hexPreviewStyle = (hexDraft) => {
  const rgb = parseHexColor(hexDraft);
  return rgb ? { background: `rgba(${rgb.join(',')}, 0.85)` } : undefined;
};
